import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment';
import {
  displayUserName,
  formatNumbers,
  newFormatPrice,
  ORDER_HISTORY_TYPES,
  ORDER_STATUSES,
} from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import OrderHistoryProp from '../propTypes/orderHistoryProp';
import {
  OPPORTUNITIES_STATUSES,
  OPPORTUNITY_HISTORY_TYPES,
} from '../../opportunities/components/helpers/opportunitiesListHelper';
import ConfigsContext from '../../../contextProviders/ConfigsContext';

const getBadge = timelineStatus => {
  switch (timelineStatus) {
    case 'pending':
      return 'warning';
    case 'pending_payment':
      return ' status-pending_payment';
    case 'processing':
      return 'info';
    case 'processing_error':
      return ' status-processing_error';
    case 'completed':
    case 'approved':
      return 'success';
    case 'canceled':
    case 'rejected':
      return 'danger';
    case 'refunded':
      return 'secondary';
    case 'failed':
      return 'dark';
    case 'lost':
      return ' status-lost';
    case 'out_of_stock':
      return ' status-out_of_stock';
    default:
      return 'warning';
  }
};

function TimelineStatus({ t, timelineItem, currency, isOpportunity }) {
  const { _id, status, type, user, date, metadata } = timelineItem;

  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings, directSelling } = tenant?.settings;
  const { CVIntegration, VAIntegration } = directSellingSettings || {};

  const showVA = directSelling && VAIntegration;
  const showCV = directSelling && CVIntegration;

  const statuses = useMemo(
    () =>
      isOpportunity
        ? OPPORTUNITIES_STATUSES.find(s => s.status === status)?.text
        : `status.${ORDER_STATUSES.find(s => s.status === status)?.text}`,
    [],
  );

  // Text shown in the timeline item
  const timelineSummary = useMemo(() => {
    if (isOpportunity) {
      return OPPORTUNITY_HISTORY_TYPES.find(s => s.type === type)?.text
        ? t(OPPORTUNITY_HISTORY_TYPES.find(s => s.type === type)?.text)
        : '';
    }
    return ORDER_HISTORY_TYPES.find(s => s.type === type)?.text
      ? t(ORDER_HISTORY_TYPES.find(s => s.type === type)?.text)
      : '';
  }, []);
  const timelineDate = moment(date).format('DD MMM YYYY - HH:mm');
  const timelineStatusChange = t(`${statuses}`);
  let timelineUser = '';
  if (user) {
    timelineUser =
      user?.username || user?.firstName || user?.lastName
        ? displayUserName(user)
        : `(${t('reports.marketplace.orders.user.removed')})`;
  }

  const isRefund = status === 'refunded';
  const showInfoBadge = type === 'status_change' || isRefund;
  const textRefund =
    isRefund && metadata?.refund
      ? `${t('shop.refunded')}: ${newFormatPrice(
          metadata.refund,
          true,
          currency,
        )}`
      : '';

  const renderDirectSellingRefund = (isVA, value) => {
    const label = isVA ? t('shop.refunded.va') : t('shop.refunded.cv');
    const valueStr = value
      ? `${label}: ${formatNumbers(value, false, true)}`
      : '';

    return (
      valueStr && (
        <>
          <br />
          <span>{valueStr}</span>
        </>
      )
    );
  };

  return (
    <>
      <div key={_id} className="m-list-timeline__item fix-new-status">
        <span
          className={`m-list-timeline__badge m-list-timeline__badge--${getBadge(
            status,
          )}`}
        />
        <span className="m-list-timeline__text">{timelineSummary}</span>
        <span className="m-list-timeline__time">{timelineDate}</span>
      </div>
      {isRefund && (metadata?.refundVA || metadata?.refundCV) && (
        <div className="refund-info">
          <span>{textRefund}</span>
          {showVA && renderDirectSellingRefund(true, metadata?.refundVA)}
          {showCV && renderDirectSellingRefund(false, metadata?.refundCV)}
        </div>
      )}
      {showInfoBadge && (
        <div
          id="badge-info-history"
          className="m-badge-timeline mb-2 badge-padding"
        >
          <span className={`badge badge-pill badge-${getBadge(status)}`}>
            {timelineStatusChange}
          </span>
        </div>
      )}
      {user ? (
        <>
          <div id="user-info-history">
            <img
              src={user?.image?.thumb || '/images/no_user.png'}
              onError={e => {
                e.target.onerror = null;
                e.target.src = '/images/no_user.png';
              }}
              alt=""
            />
            <span className="ml-2">{timelineUser}</span>
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
}

TimelineStatus.propTypes = {
  /** Translation function */
  t: PropTypes.func.isRequired,
  timelineItem: PropTypes.shape(OrderHistoryProp.propType).isRequired,
  currency: PropTypes.string.isRequired,
  isOpportunity: PropTypes.bool,
};

TimelineStatus.defaultProps = {
  isOpportunity: false,
};

export default translate(TimelineStatus);
